import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, viewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, take, takeUntil } from 'rxjs';

import { whenTruthy } from 'ish-core/utils/operators';

import { CustomQuickorderFacade } from '../../facades/custom-quickorder.facade';
import { QuickOrder, QuickOrderItem } from '../../models/custom-quickorder/custom-quickorder.model';

/**
 * The Quick Add Products Component displays a form to insert multiple product sku and quantity to add them to the cart.
 */
@Component({
  selector: 'custom-quickorder-add-products-form',
  templateUrl: './custom-quickorder-add-products-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CustomQuickorderAddProductsFormComponent implements OnInit, OnDestroy {
  quickOrderForm: FormGroup = new FormGroup({});

  options: FormlyFormOptions = {};
  fields: FormlyFieldConfig[];
  quickOrder$: Observable<QuickOrder>;
  numberOfRows: number;
  model: { addProducts: QuickOrderItem[] } = { addProducts: [] };
  basketMaxSize: number;

  private destroy$ = new Subject<void>();
  readonly validateQuickOrderForm = viewChild<ElementRef<HTMLElement>>('validateQuickOrderForm');
  inValidForm: boolean;

  constructor(private translate: TranslateService, private quickOrderFacade: CustomQuickorderFacade) {}

  ngOnInit() {
    this.initModel();
    this.quickOrder$ = this.quickOrderFacade.getQuickOrder$();

    this.fields = this.getFields();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  reset() {
    this.options.resetModel();
    this.initModel();
  }

  onAddProducts() {
    this.quickOrder$.pipe(whenTruthy(), take(1), takeUntil(this.destroy$)).subscribe(quickorder => {
      const products = quickorder.items.filter((p: QuickOrderItem) => !!p.productSKU && !!p.quantity);
      if (products.length > 0) {
        const quickOrderItems = products.map((record: { productSKU: any; quantity: number }) => ({
          sku: record.productSKU,
          quantity: +record.quantity,
        }));
        this.quickOrderFacade.addQuickOrderToBasket(quickOrderItems);
      }
      this.reset();
    });
  }
  isInvalidForm() {
    const htmlElement: HTMLElement = this.validateQuickOrderForm()?.nativeElement;
    const validationMessageClass = htmlElement?.querySelectorAll('.validation-message');
    this.inValidForm = false;
    if (validationMessageClass?.length) {
      this.inValidForm = true;
    }
    if (!this.isQuickOrderItemPresent()) {
      this.inValidForm = true;
    }
  }

  private initModel() {
    this.model = { addProducts: [] };
    for (let i = 0; i < this.numberOfRows; i++) {
      this.model.addProducts.push({ productSKU: '', quantity: 1 });
    }
  }
  private isQuickOrderItemPresent(): boolean {
    for (let i = 0; i < this.numberOfRows; i++) {
      if (this.model.addProducts[i]?.productSKU !== '') {
        return true;
      }
    }
    return false;
  }

  /**
   * returns the field with a repeating type
   * repeat contains the representing of the form with form fields and links to add and remove lines (check quickorder-repeat-form.component for more information)
   * the field array give the input field with validators of the sku for each line (which are represented by the objects of the model array)
   */
  private getFields(): FormlyFieldConfig[] {
    this.quickOrderFacade.basketMaxItemSize$
      .pipe(whenTruthy(), take(1), takeUntil(this.destroy$))
      .subscribe(basketMaxSize => (this.basketMaxSize = basketMaxSize));
    if (this.basketMaxSize <= 10) {
      this.numberOfRows = this.basketMaxSize;
    } else {
      this.numberOfRows = 10;
    }
    return [
      {
        key: 'addProducts',
        type: 'custom-repeat',
        props: {
          addText: 'quickorder.page.add.row',
          addMoreText: 'quickorder.page.add.row.multiple',
          numberMoreRows: this.numberOfRows,
        },
        fieldArray: {
          fieldGroupClassName: 'row list-item-row ',
          fieldGroup: [
            {
              key: 'productSKU',
              type: 'quick-order-search-input-field',
              className: 'col-12 list-item search-container',
              props: {
                fieldClass: 'col-12',
                attributes: { autocomplete: 'off' },
              },
              expressions: {
                'props.required': conf => {
                  if (conf?.model?.productSKU === '') {
                    return false;
                  } else {
                    return !!conf?.model?.productSKU && !!conf?.model?.quantity;
                  }
                },
              },
              modelOptions: {
                updateOn: 'blur',
              },
              validation: {
                messages: {
                  existingProduct: (_, field) =>
                    this.translate.get('dias.quickorder.sku.alreadyexists.error', {
                      0: this.model?.addProducts[parseInt(field.parent.key.toString(), 10)]?.productSKU,
                      1: this.getSKUindex(
                        this.model?.addProducts[parseInt(field.parent.key.toString(), 10)]?.productSKU
                      ),
                    }),
                  validProduct: (_, field) =>
                    this.translate.get('dias.quickorder.page.error.invalid.product', {
                      0: field.parent?.fieldGroup[0]?.formControl?.value,
                    }),
                },
              },
            },
          ],
        },
      },
    ];
  }

  getSKUindex(sku: string): number {
    let index = 0;
    this.quickOrder$.pipe(take(1), takeUntil(this.destroy$)).subscribe(quickorderdata => {
      const items = quickorderdata.items;

      for (let i = 0; i < this.model?.addProducts?.length; i++) {
        if (items[i] && items[i]?.productSKU === sku) {
          index = items[i]?.position;
        }
      }
    });
    return index;
  }
}
